/**
 * Screener definitions shared by the scan table: the filter panel, the column
 * sets of each scan type and the column groups. Labels and definitions are
 * vue-i18n keys (see assets/lang/en.json and fr.json).
 */

// "between" is a range: `value` is the lower bound, `valueMax` the upper one.
export const BETWEEN = "between";
const SIGNS = ["<", ">", "=", BETWEEN];

// Most common reporting currencies among the stocks refreshed in the last week (Oct 2026).
const CURRENCIES = ["USD", "CAD", "EUR", "GBP", "JPY", "AUD", "HKD", "CHF", "SEK", "CNY", "NOK", "BRL", "ZAR", "MXN", "SGD", "DKK", "ILS"];

/** Most rows a scan request returns (the backend's page size). */
export const RESULT_CAP = 5000;

// "All Stocks": the whole stock universe rather than one scan's results.
export const UNIVERSE_SCAN = "All Stocks";
// Filters the universe starts with (still editable). Currency stays on All.
export const UNIVERSE_DEFAULT_FILTERS = { exchange: "NO_OTC" };

// Leading industries, then trend-template stocks in them (the backend's TopDownScanService).
export const TOP_DOWN_SCAN = "Top-Down Scan";

// Option combo scans (the backend's OptionComboScanService): one row per stock with the combo it proposes.
export const STRADDLE_SCAN = "Earnings Straddle Scan";
export const PUT_CREDIT_SCAN = "Put Credit Spread Scan";
export const CALL_DEBIT_SCAN = "Call Debit Spread Scan";
export const COMBO_SCANS = [STRADDLE_SCAN, PUT_CREDIT_SCAN, CALL_DEBIT_SCAN];

// Calendar shortcuts of the date filters: a range of days from today (`from` and `to` are day offsets, inclusive).
const UPCOMING_DAYS = [
  { label: "Today", from: 0, to: 0 },
  { label: "Next 7 days", from: 0, to: 7 },
  { label: "Next 14 days", from: 0, to: 14 },
  { label: "Next 30 days", from: 0, to: 30 },
];
const PAST_DAYS = [
  { label: "Last 30 days", from: -30, to: 0 },
  { label: "Last 90 days", from: -90, to: 0 },
  { label: "Last 12 months", from: -365, to: 0 },
];

const ABOVE_BELOW = [{ key: "all", value: "All" }, { key: "above", value: "Above" }, { key: "below", value: "Below" }];

// Filters in the order they appear in the panel. `key` is the backend field
// and doubles as the URL query parameter and the unique id.
const FILTERS = [
  {
    name: "Descriptive",
    selects: [
      { key: "symbol", label: "Symbol", isText: true },
      {
        key: "exchange", label: "Exchange", isDropdown: true,
        list: [
          { key: "all", value: "All" },
          { key: "XNYS", value: "New York Stock Exchange (NYSE)" },
          { key: "XASE", value: "NYSE American (formerly American Stock Exchange)" },
          { key: "BATS", value: "BATS Exchange" },
          { key: "XNAS", value: "NASDAQ Stock Market" },
          { key: "OOTC", value: "Over-the-Counter (OTC)" },
          { key: "NO_OTC", value: "All except OTC" },
        ],
      },
      // Market caps are stored in the company's reporting currency: compare like with like.
      {
        key: "currency", label: "Currency", isDropdown: true,
        list: [{ key: "all", value: "All" }, ...CURRENCIES.map((c) => ({ key: c, value: c }))],
      },
      // Finnhub security types, grouped (the backend's ScanTypes.SecurityTypes). ETFs and other non-shares have no
      // reporting currency: with one of those types the backend lets them through the Currency filter.
      {
        key: "securityType", label: "Security Type", isDropdown: true,
        list: [
          { key: "all", value: "All" },
          { key: "stocks", value: "Stocks (common, ADR, REIT)" },
          { key: "common", value: "Common stocks" },
          { key: "adr", value: "ADRs" },
          { key: "etp", value: "ETFs & ETPs" },
          { key: "reit", value: "REITs" },
          { key: "fund", value: "Closed-end & mutual funds" },
          { key: "preferred", value: "Preferreds & notes" },
          { key: "warrant", value: "Warrants" },
          { key: "unit", value: "Units" },
          { key: "right", value: "Rights" },
        ],
      },
      { key: "mktCap", label: "Market Cap", sign: ">" },
      { key: "earningDate", label: "Earning Date", isDate: true, sign: ">", presets: UPCOMING_DAYS },
      // Calendar days to the next report (the "Days to Earnings" column); another way to use the picker above.
      { key: "joursEarnings", label: "JoursEarnings", sign: "<" },
      // Time of that next report, as stored in EarningHour (the backend's ScanTypes.EarningHours).
      {
        key: "earningHour", label: "Report Time", def: "def-earning-hour", isDropdown: true,
        list: [
          { key: "all", value: "All" },
          { key: "bmo", value: "Before open" },
          { key: "amc", value: "After close" },
          { key: "dmh", value: "During market hours" },
        ],
      },
      { key: "ipo", label: "IPO Date", isDate: true, sign: ">", presets: PAST_DAYS },
      { key: "price", label: "Price", sign: ">" },
      {
        key: "sector", label: "Sector", isDropdown: true,
        // GICS sectors, as Finnhub reports them (CompanyProfile.gsector, also shown on the stock page).
        list: [
          { key: "all", value: "All" },
          { key: "Communication Services", value: "Communication Services" },
          { key: "Consumer Discretionary", value: "Consumer Discretionary" },
          { key: "Consumer Staples", value: "Consumer Staples" },
          { key: "Energy", value: "Energy" },
          { key: "Financials", value: "Financials" },
          { key: "Health Care", value: "Health Care" },
          { key: "Industrials", value: "Industrials" },
          { key: "Information Technology", value: "Information Technology" },
          { key: "Materials", value: "Materials" },
          { key: "Real Estate", value: "Real Estate" },
          { key: "Utilities", value: "Utilities" },
        ],
      },
      { key: "dividendYield", label: "Dividend Yield", sign: ">" },
      // Next declared dividend (listed payers, refreshed every few nights): the ex-dividend day and the next payment.
      { key: "dividendExDate", label: "Ex-Dividend Date", def: "def-ex-dividend", isDate: true, sign: ">", presets: UPCOMING_DAYS },
      { key: "dividendPayDate", label: "Dividend Pay Date", def: "def-dividend-pay", isDate: true, sign: ">", presets: UPCOMING_DAYS },
      { key: "currentVolume", label: "Current Volume", sign: ">" },
      { key: "avgVolume10Day", label: "Average Volume", sign: ">" },
      // Latest volume / 25-day average volume: 1 = average, 2 = twice the average.
      { key: "relVolume", label: "Relative Volume", sign: ">" },
      {
        key: "finnhubindustry", label: "Industry", isDropdown: true,
        list: [
          { key: "all", value: "All" },
          { key: "Aerospace & Defense", value: "Aerospace & Defense" },
          { key: "Air Freight & Logistics", value: "Air Freight & Logistics" },
          { key: "Auto Components", value: "Auto Components" },
          { key: "Automobiles", value: "Automobiles" },
          { key: "Banks", value: "Banks" },
          { key: "Beverages", value: "Beverages" },
          { key: "Biotechnology", value: "Biotechnology" },
          { key: "Broadline Retail", value: "Broadline Retail" },
          { key: "Building Products", value: "Building Products" },
          { key: "Capital Markets", value: "Capital Markets" },
          { key: "Chemicals", value: "Chemicals" },
          { key: "Commercial Services & Supplies", value: "Commercial Services & Supplies" },
          { key: "Communications Equipment", value: "Communications Equipment" },
          { key: "Construction & Engineering", value: "Construction & Engineering" },
          { key: "Construction Materials", value: "Construction Materials" },
          { key: "Consumer Finance", value: "Consumer Finance" },
          { key: "Consumer Staples Distribution & Retail", value: "Consumer Staples Distribution & Retail" },
          { key: "Containers & Packaging", value: "Containers & Packaging" },
          { key: "Distributors", value: "Distributors" },
          { key: "Diversified Consumer Services", value: "Diversified Consumer Services" },
          { key: "Diversified REITs", value: "Diversified REITs" },
          { key: "Diversified Telecommunication Services", value: "Diversified Telecommunication Services" },
          { key: "Electric Utilities", value: "Electric Utilities" },
          { key: "Electrical Equipment", value: "Electrical Equipment" },
          { key: "Electronic Equipment, Instruments & Components", value: "Electronic Equipment, Instruments & Components" },
          { key: "Energy Equipment & Services", value: "Energy Equipment & Services" },
          { key: "Entertainment", value: "Entertainment" },
          { key: "Financial Services", value: "Financial Services" },
          { key: "Food Products", value: "Food Products" },
          { key: "Gas Utilities", value: "Gas Utilities" },
          { key: "Ground Transportation", value: "Ground Transportation" },
          { key: "Health Care Equipment & Supplies", value: "Health Care Equipment & Supplies" },
          { key: "Health Care Providers & Services", value: "Health Care Providers & Services" },
          { key: "Health Care REITs", value: "Health Care REITs" },
          { key: "Health Care Technology", value: "Health Care Technology" },
          { key: "Hotel & Resort REITs", value: "Hotel & Resort REITs" },
          { key: "Hotels, Restaurants & Leisure", value: "Hotels, Restaurants & Leisure" },
          { key: "Household Durables", value: "Household Durables" },
          { key: "Household Products", value: "Household Products" },
          { key: "Independent Power and Renewable Electricity Producers", value: "Independent Power and Renewable Electricity Producers" },
          { key: "Industrial Conglomerates", value: "Industrial Conglomerates" },
          { key: "Industrial REITs", value: "Industrial REITs" },
          { key: "Insurance", value: "Insurance" },
          { key: "Interactive Media & Services", value: "Interactive Media & Services" },
          { key: "IT Services", value: "IT Services" },
          { key: "Leisure Products", value: "Leisure Products" },
          { key: "Life Sciences Tools & Services", value: "Life Sciences Tools & Services" },
          { key: "Machinery", value: "Machinery" },
          { key: "Marine Transportation", value: "Marine Transportation" },
          { key: "Media", value: "Media" },
          { key: "Metals & Mining", value: "Metals & Mining" },
          { key: "Mortgage Real Estate Investment Trusts (REITs)", value: "Mortgage Real Estate Investment Trusts (REITs)" },
          { key: "Multi-Utilities", value: "Multi-Utilities" },
          { key: "Office REITs", value: "Office REITs" },
          { key: "Oil, Gas & Consumable Fuels", value: "Oil, Gas & Consumable Fuels" },
          { key: "Paper & Forest Products", value: "Paper & Forest Products" },
          { key: "Passenger Airlines", value: "Passenger Airlines" },
          { key: "Personal Care Products", value: "Personal Care Products" },
          { key: "Pharmaceuticals", value: "Pharmaceuticals" },
          { key: "Professional Services", value: "Professional Services" },
          { key: "Real Estate Management & Development", value: "Real Estate Management & Development" },
          { key: "Residential REITs", value: "Residential REITs" },
          { key: "Retail REITs", value: "Retail REITs" },
          { key: "Semiconductors & Semiconductor Equipment", value: "Semiconductors & Semiconductor Equipment" },
          { key: "Software", value: "Software" },
          { key: "Specialized REITs", value: "Specialized REITs" },
          { key: "Specialty Retail", value: "Specialty Retail" },
          { key: "Technology Hardware, Storage & Peripherals", value: "Technology Hardware, Storage & Peripherals" },
          { key: "Textiles, Apparel & Luxury Goods", value: "Textiles, Apparel & Luxury Goods" },
          { key: "Tobacco", value: "Tobacco" },
          { key: "Trading Companies & Distributors", value: "Trading Companies & Distributors" },
          { key: "Transportation Infrastructure", value: "Transportation Infrastructure" },
          { key: "Water Utilities", value: "Water Utilities" },
          { key: "Wireless Telecommunication Services", value: "Wireless Telecommunication Services" },
        ],
      },
      { key: "shareOutstanding", label: "Shares Outstanding", sign: ">" },
    ],
  },
  {
    name: "Technicals",
    selects: [
      { key: "beta", label: "Beta", sign: ">" },
      { key: "atr", label: "Average True Range (ATR)", sign: ">" },
      { key: "rsI14", label: "RSI (14)", sign: ">" },
      { key: "twentyDaySMA", label: "20-Days Simple Moving Average", sign: ">" },
      { key: "fiftyDaySMA", label: "50-Days Simple Moving Average", sign: ">" },
      { key: "smA200", label: "200-Days Simple Moving Average", sign: ">" },
      // The latest close compared with its moving average (the filters above compare the average itself).
      { key: "priceVsSma20", label: "Price vs SMA20", isDropdown: true, list: ABOVE_BELOW },
      { key: "priceVsSma50", label: "Price vs SMA50", isDropdown: true, list: ABOVE_BELOW },
      { key: "priceVsSma200", label: "Price vs SMA200", isDropdown: true, list: ABOVE_BELOW },
      // The latest day's move: close vs previous close (signed), and the opening gap (unsigned, see GapIndicators).
      { key: "changePct", label: "Change%", def: "def-change-pct", sign: ">" },
      { key: "gapPct", label: "Gap %", def: "def-gap-pct", sign: ">" },
      // Keys are the scanner's stored TypeGap values (the backend's ScanTypes.GapTypes).
      {
        key: "gapType", label: "Gap Type", def: "def-gap-type", isDropdown: true,
        list: [
          { key: "all", value: "All" },
          { key: "Ordinary", value: "Ordinary" },
          { key: "Significant", value: "Significant" },
          { key: "Dead Cat Bounce", value: "Dead Cat Bounce" },
        ],
      },
      // Candlestick pattern of the latest bar; keys are the scanner's stored names.
      {
        key: "pattern", label: "Candle Type", isDropdown: true,
        list: [
          { key: "all", value: "All" },
          { key: "Bullish Engulfing", value: "Bullish Engulfing" },
          { key: "Bearish Engulfing", value: "Bearish Engulfing" },
          { key: "Hammer", value: "Hammer" },
          { key: "HangingMan", value: "Hanging Man" },
          { key: "Inverted Hammer", value: "Inverted Hammer" },
          { key: "Piercing", value: "Piercing" },
          { key: "Shooting Star", value: "Shooting Star" },
          { key: "Dark Cloud", value: "Dark Cloud" },
        ],
      },
      // Latest close vs its 21-day EMA (the Top-Down scan's Entry column), stored for every stock with a year of history.
      {
        key: "topDownEntry", label: "Entry", def: "def-td-entry", isDropdown: true,
        list: [
          { key: "all", value: "All" },
          { key: "at_ema21", value: "At EMA21" },
          { key: "deep_pullback", value: "Deep pullback" },
          { key: "extended", value: "Extended" },
        ],
      },
      { key: "Is52WeekHigh", label: "52-Week High", isCheckbox: true },
      { key: "Is52WeekLow", label: "52-Week Low", isCheckbox: true },
    ],
  },
  {
    // Options data of the nightly refresh. Stocks without options (values 0) never match the number filters.
    name: "Options",
    selects: [
      // In percent: 50 means 50% (stored as 0.5).
      { key: "iv", label: "Implied Volatility (%)", def: "def-iv", sign: ">" },
      { key: "ivRank", label: "IV Rank", def: "def-iv-rank", sign: ">" },
      { key: "ivRankPlus", label: "IV Rank+", def: "def-iv-rank-plus", sign: ">" },
      // Nightly OSS scan, at the best expiration. Stored on Sweet Spot rows only: matches nothing on the other scans.
      { key: "strangleIvHv60", label: "Strangle IV / HV60", def: "def-strangle-iv-hv60", sign: "<" },
      // Vol points (6 = 6 IV points).
      { key: "putCallSkew", label: "Put − Call IV (pts)", def: "def-put-call-skew", sign: "<" },
      { key: "weeklyOptions", label: "Weekly options", def: "def-weekly-options", isCheckbox: true },
    ],
  },
  {
    name: "Fundamental",
    selects: [
      { key: "peRatio", label: "P/E Ratio", sign: ">" },
      { key: "epsGrowth5Y", label: "EPS growth past 5 years", sign: ">" },
      { key: "roaTtm", label: "Return on Assets", sign: ">" },
      { key: "longTermDebtEquityQuarterly", label: "LT Debt/Equity", sign: ">" },
      { key: "payoutRatioTtm", label: "Payout Ratio", sign: ">" },
      { key: "roeTtm", label: "Return on Equity", sign: ">" },
      { key: "epsAnnual", label: "EPS growth this year", sign: ">" },
      { key: "epsGrowthQuarterlyYoy", label: "EPS growth qtr over qtr", sign: ">" },
      { key: "roiTtm", label: "Return on Investment", sign: ">" },
      { key: "grossMarginTTM", label: "Gross Margin", sign: ">" },
      { key: "psTtm", label: "P/S", sign: ">" },
      { key: "revenueGrowthQuarterlyYoy", label: "Sales growth qtr over qtr", sign: ">" },
      { key: "revenueGrowth5Y", label: "Sales growth past 5 years", sign: ">" },
      { key: "currentRatioQuarterly", label: "Currently Ratio", sign: ">" },
      { key: "operationMarginTtm", label: "Operating Margin", sign: ">" },
      { key: "pbQuarterly", label: "P/B", sign: ">" },
      { key: "quickRatioQuarterly", label: "Quick Ratio", sign: ">" },
      { key: "netProfitMarginTtm", label: "Net Profit Margin", sign: ">" },
    ],
  },
];

/**
 * Replaces the Industry filter's options with the backend's (GET /scan/industries: Finnhub labels or GICS industries,
 * whichever classification it uses). Returns the new option list, or null when `industries` is empty.
 */
export function setIndustryOptions(industries) {
  if (!Array.isArray(industries) || industries.length === 0) return null;
  const select = FILTERS[0].selects.find((s) => s.key === "finnhubindustry");
  select.list = [{ key: "all", value: "All" }, ...industries.map((i) => ({ key: i, value: i }))];
  return select.list.map((o) => ({ ...o }));
}

/**
 * A fresh, mutable copy of the filter panel. Used for the initial state and for
 * "Reset filters", so both always match. `currentVal` is the comparison sign
 * (numbers, dates) or the selected option label (dropdowns); `value` is the input,
 * and `valueMax` the second input of a "between" number or date filter.
 */
export function createFilters() {
  return FILTERS.map((category) => ({
    name: category.name,
    selects: category.selects.map((s) => {
      const select = { ...s, id: s.key, value: s.isCheckbox ? false : "" };
      if (s.isDropdown) {
        select.list = s.list.map((o) => ({ ...o }));
        select.currentVal = "All";
      } else if (!s.isText && !s.isCheckbox) {
        // Dates compare like numbers (whole days).
        select.list = [...SIGNS];
        select.currentVal = s.sign;
        select.valueMax = "";
      } else {
        select.currentVal = "";
      }
      delete select.sign;
      return select;
    }),
  }));
}

// ---- Columns -------------------------------------------------------------------
// `value` is the header label key, `def` an optional tooltip key.
const col = (key, value, def) => (def ? { key, value, def } : { key, value });

const SYMBOL = col("symbol", "Symbol");
const COMPANY = col("companyName", "Company Name");
const CLOSE = col("close", "Close");
const VOLUME = col("volume", "Volume");
const ATR = col("atr", "ATR", "def-atr");

export const scanDefinition = {
  "Candlestick Scan": [SYMBOL, COMPANY, CLOSE, VOLUME, col("typeChandelle", "Candle Type"), ATR, col("pav", "PAV", "def-pav")],
  "Gaps Scan": [
    SYMBOL, COMPANY, CLOSE, VOLUME,
    col("typeGap", "Type Gap"), col("gap_percent", "Gap %"), col("gap_vol_percent", "Gap Vol %"),
    col("iv", "IV"), col("ivRank", "IV Rank"), col("ivRankPlus", "IV Rank+"), ATR,
  ],
  "Sweet Spot Scan": [
    SYMBOL, COMPANY, CLOSE,
    col("oss", "OSS", "def-oss"),
    col("ossBestContract", "Best Contract", "def-best-contract"),
    col("ossBestDte", "Best DTE", "def-best-dte"),
    col("ossBestExpiration", "Best Expiration"),
    col("ossCheapVsHistory", "Cheap vs History %", "def-cheap-vs-history"),
    col("iv", "IV"), col("ivRank", "IV Rank"),
    col("ivPercentileTrue", "IV Percentile", "def-iv-percentile"),
    col("jourEarnings", "JoursEarnings"), col("nextEarningDate", "Next Earnings Date"),
    col("ossIvCheapness", "IV Cheapness", "def-iv-cheapness"),
    col("ossEarningsCycle", "Earnings Cycle", "def-earnings-cycle"),
    col("ossDteOpportunity", "DTE Opportunity", "def-dte-opportunity"),
    col("ossTermStructure", "Term Structure", "def-term-structure"),
    col("ossLiquidity", "Liquidity", "def-liquidity"),
    col("ossSkew", "Skew", "def-skew"),
    col("ossSkewDirection", "Skew Direction"),
    VOLUME,
  ],
  [TOP_DOWN_SCAN]: [
    SYMBOL,
    col("tdGroup", "Group", "def-td-group"),
    col("tdRs", "RS", "def-td-rs"),
    CLOSE,
    col("mktcap", "Market Cap"),
    col("tdFromHigh", "% from 52W High"),
    col("tdAboveLow", "% above 52W Low"),
    col("tdDistEma21", "Dist. EMA21", "def-td-dist-ema21"),
    col("tdEntry", "Entry", "def-td-entry"),
    col("relVolume", "Vol×", "def-td-vol"),
    col("tdGrade", "Fundamentals", "def-td-grade"),
  ],
  [STRADDLE_SCAN]: [
    SYMBOL, COMPANY, CLOSE,
    col("nextEarningDate", "Next Earnings Date"),
    col("cbSessions", "Sessions to earnings", "def-cb-sessions"),
    col("cbExit", "Sell by", "def-cb-exit"),
    col("cbLegs", "Combo", "def-cb-legs"),
    col("cbExpiration", "Expiration"),
    col("cbDte", "DTE"),
    col("cbMid", "Cost", "def-cb-mid"),
    col("cbNatural", "Natural", "def-cb-natural"),
    col("cbSlippage", "Spread cost", "def-cb-slippage"),
    col("cbIvVsPreReport", "IV vs pre-report level", "def-cb-iv-vs-pre-report"),
    col("cbImpliedMove", "Implied move", "def-cb-implied-move"),
    col("cbTheta", "Theta/day", "def-cb-theta"),
    col("relVolume", "Vol×", "def-td-vol"),
    col("ivRank", "IV Rank"),
  ],
  [PUT_CREDIT_SCAN]: [
    SYMBOL, COMPANY, CLOSE,
    col("cbRs", "RS", "def-td-rs"),
    col("tdDistEma21", "Dist. EMA21", "def-td-dist-ema21"),
    col("cbLegs", "Combo", "def-cb-legs"),
    col("cbExpiration", "Expiration"),
    col("cbDte", "DTE"),
    col("cbMid", "Credit", "def-cb-mid"),
    col("cbNatural", "Natural", "def-cb-natural"),
    col("cbCreditPct", "Credit / Width", "def-cb-credit-pct"),
    col("cbMaxLoss", "Max loss", "def-cb-max-loss"),
    col("cbBreakeven", "Breakeven"),
    col("cbPop", "POP", "def-cb-pop"),
    col("cbSigmas", "Short σ", "def-cb-sigmas"),
    col("cbIvHv", "IV / HV30", "def-cb-iv-hv"),
    col("ivRank", "IV Rank"),
    col("relVolume", "Vol×", "def-td-vol"),
    col("nextEarningDate", "Next Earnings Date"),
  ],
  [CALL_DEBIT_SCAN]: [
    SYMBOL, COMPANY, CLOSE,
    col("cbRs", "RS", "def-td-rs"),
    col("tdDistEma21", "Dist. EMA21", "def-td-dist-ema21"),
    col("cbLegs", "Combo", "def-cb-legs"),
    col("cbExpiration", "Expiration"),
    col("cbDte", "DTE"),
    col("cbMid", "Debit", "def-cb-mid"),
    col("cbNatural", "Natural", "def-cb-natural"),
    col("cbMaxGain", "Max gain"),
    col("cbRewardRisk", "Reward / Risk", "def-cb-reward-risk"),
    col("cbBreakeven", "Breakeven"),
    col("cbPop", "POP", "def-cb-pop"),
    col("ivRank", "IV Rank"),
    col("relVolume", "Vol×", "def-td-vol"),
    col("nextEarningDate", "Next Earnings Date"),
  ],
  "Combo Scan": [
    SYMBOL, COMPANY, CLOSE, VOLUME,
    col("iv", "IV"), col("ivRank", "IV Rank"), col("ivRankPlus", "IV Rank+"), ATR,
    col("jourEarnings", "JoursEarnings"), col("pav", "PAV", "def-pav"),
    col("gapHaussMoyen", "Avg gap up $"), col("gapHaussMoyenPercent", "Avg gap up %"),
    col("gapBaissMoyen", "Avg gap down $"), col("gapBaissMoyenPercent", "Avg gap down %"),
    col("mouvHauss", "6-day up move $"), col("mouvHaussPercent", "6-day up move %"),
    col("mouvBaiss", "6-day down move $"), col("mouvBaissPercent", "6-day down move %"),
  ],
};

// Column groups shown next to the scan columns ("Scan" is filled per scan type).
export const columnGroups = [
  { name: "Overview", colItems: [SYMBOL, COMPANY, col("sector", "Sector"), col("industry", "Industry"), col("exchange", "Exchange"), col("securityType", "Type"), col("mktcap", "Market Cap"), CLOSE, col("chgPer", "Change%"), VOLUME] },
  { name: "Financial", colItems: [SYMBOL, COMPANY, col("dividend", "Dividend"), col("dividendExDate", "Ex-Dividend Date", "def-ex-dividend"), col("dividendAmount", "Next Dividend"), col("dividendPayDate", "Dividend Pay Date", "def-dividend-pay"), col("mktcap", "Market Cap"), col("roa", "ROA"), col("roe", "ROE"), col("roi", "ROI"), col("nextEarningDate", "Next Earnings Date"), CLOSE, col("chgPer", "Change%"), VOLUME] },
  { name: "Performance", colItems: [SYMBOL, COMPANY, col("perfYtd", "Perf YTD"), col("avgVolume10day", "10 Days Avg Trading Volume"), col("avgVolume3month", "3 Months Avg Trading Volume"), col("gap_percent", "Gap %", "def-gap-pct"), col("typeGap", "Gap Type", "def-gap-type"), CLOSE, col("chgPer", "Change%"), VOLUME] },
  { name: "Technical", colItems: [SYMBOL, COMPANY, col("sma20", "SMA20"), col("sma50", "SMA50"), col("sma200", "SMA200"), ATR, col("rsi", "RSI"), col("typeChandelle", "Candle Type"), col("relVolume", "Rel. Volume"), col("52w_high", "52W High"), col("52w_low", "52W Low"), CLOSE, col("chgPer", "Change%"), VOLUME] },
  // IV as a percent here (ivPct); the scan columns keep showing the stored decimal (iv).
  { name: "Options", colItems: [SYMBOL, COMPANY, col("ivPct", "IV", "def-iv"), col("ivRank", "IV Rank", "def-iv-rank"), col("ivRankPlus", "IV Rank+", "def-iv-rank-plus"), col("weeklyOptions", "Weekly options", "def-weekly-options"), col("strangleIvHv60", "Strangle IV / HV60", "def-strangle-iv-hv60"), col("nextEarningDate", "Next Earnings Date"), CLOSE, col("chgPer", "Change%"), VOLUME] },
];

// Type column: a row's Finnhub security type as its group in the Security Type filter. Types only "Stocks" covers
// (NY Reg Shrs, Tracking Stk, MLP...) and unknown ones show as stored.
export const SECURITY_TYPE_LABELS = {
  "Common Stock": "Common stocks",
  ADR: "ADRs",
  ETP: "ETFs & ETPs",
  REIT: "REITs",
  "Closed-End Fund": "Closed-end & mutual funds",
  "Open-End Fund": "Closed-end & mutual funds",
  PUBLIC: "Preferreds & notes",
  "Equity WRT": "Warrants",
  Unit: "Units",
  Right: "Rights",
};

/** Backend `scanName` for each scan selector label (the label's first word is not a safe key). */
export const scanNameMap = {
  "Candlestick Scan": "candlestick",
  "Combo Scan": "combo",
  "Gaps Scan": "gaps",
  "Sweet Spot Scan": "sweetspot",
  [TOP_DOWN_SCAN]: "topdown",
  [STRADDLE_SCAN]: "straddle",
  [PUT_CREDIT_SCAN]: "putcredit",
  [CALL_DEBIT_SCAN]: "calldebit",
  [UNIVERSE_SCAN]: "universe",
};
